import { Button, CameraCapture, cameraAvailable, Modal, IconButton } from '@hue/design-system';
import { useQuery } from '@tanstack/react-query';
import {
  Camera,
  ClipboardPaste,
  FileText,
  ImageIcon,
  ShieldAlert,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { getApiError, type ScanStatus } from '../lib/api.js';
import { formatDate } from '../lib/time-zone.js';
import { RequestAlert } from './data-view.js';
import { nextPhotoName, pasteShortcut, readImagesFromClipboard } from './receipt-staging.js';

const ACCEPTED = 'application/pdf,image/jpeg,image/png,image/webp';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/// O que a lista de comprovantes precisa saber de um arquivo guardado, venha de que módulo vier.
export interface StoredReceipt {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  scanStatus: ScanStatus;
  uploadedBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
  origin?: 'UPLOAD' | 'LINK';
}

/// Como o dono do comprovante fala com o servidor. Cada módulo guarda os seus arquivos na própria
/// tabela e rota; a tela de enviar, fotografar, colar e remover é a mesma.
export interface ReceiptsSource {
  queryKey: readonly unknown[];
  list: () => Promise<StoredReceipt[]>;
  attach: (file: File) => Promise<unknown>;
  remove: (id: string) => Promise<void>;
  fileUrl: (id: string) => string;
}

/// Comprovantes de um registro: enviar arquivo, colar da área de transferência ou arrastar para a
/// caixa. O tipo é conferido no servidor pelo conteúdo, então nada aqui depende do que o navegador
/// diz sobre o arquivo.
export function ReceiptsDialog({
  source,
  description,
  emptyText,
  timeZone,
  canManage,
  onClose,
}: {
  source: ReceiptsSource;
  description: string;
  emptyText: string;
  timeZone: string;
  canManage: boolean;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [removing, setRemoving] = useState<StoredReceipt>();
  const [cameraOpen, setCameraOpen] = useState(false);

  const receipts = useQuery({
    queryKey: source.queryKey,
    queryFn: source.list,
    retry: false,
  });

  async function upload(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    setError(undefined);
    setBusy(true);
    try {
      // Um a um: a mensagem de recusa precisa dizer QUAL arquivo o servidor não aceitou.
      for (const file of Array.from(files)) {
        await source.attach(file);
      }
      await receipts.refetch();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    void upload(event.dataTransfer.files);
  }

  /// Colar vale em qualquer ponto do diálogo, não só dentro da caixa. Pendurar o `paste` na caixa
  /// obrigaria a clicar nela antes — e ninguém clica antes de dar Ctrl+V.
  useEffect(() => {
    if (!canManage) return undefined;
    const onPaste = (event: globalThis.ClipboardEvent): void => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length === 0) return;
      event.preventDefault();
      void upload(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  });

  const list = receipts.data ?? [];

  return (
    <Modal open onOpenChange={onClose} title="Comprovantes" description={description}>
      {canManage ? (
        <div
          role="group"
          aria-label="Enviar comprovante"
          onDrop={onDrop}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          className={`mb-5 rounded-xl border border-dashed px-4 py-6 text-center transition ${
            dragging
              ? 'border-brand-500/60 bg-brand-500/10'
              : 'border-surface-border bg-surface-elevated'
          }`}
        >
          <Upload className="mx-auto mb-2 size-5 text-brand-400" />
          <div className="flex flex-col justify-center gap-2 sm:flex-row">
            {/* Botão de verdade em vez da caixa inteira clicável: o input é filho dela, e o clique
                programático voltava para o `onClick` do pai, disparando de novo em laço. */}
            <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
              Escolher arquivo
            </Button>
            {/* O comprovante nasce em papel na mão de quem comprou. Sem isto o caminho era sair do
                sistema, fotografar pelo aplicativo do aparelho e voltar para procurar o arquivo. */}
            {cameraAvailable() ? (
              <Button type="button" variant="outline" onClick={() => setCameraOpen(true)}>
                <Camera className="size-4" />
                Tirar foto
              </Button>
            ) : null}
            {/* No celular não existe Ctrl+V, e é no celular que se fotografa o comprovante. */}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void readImagesFromClipboard().then((result) => {
                  if (result.ok) void upload(result.files);
                  else setError(result.detail);
                });
              }}
            >
              <ClipboardPaste className="size-4" />
              Colar print
            </Button>
          </div>
          <p className="mt-2 text-xs text-content-muted">
            Ou arraste para cá, ou cole com {pasteShortcut()} em qualquer ponto desta janela. PDF,
            JPG, PNG ou WebP, até 10 MB.
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPTED}
            className="sr-only"
            aria-label="Arquivo do comprovante"
            onChange={(event) => void upload(event.target.files)}
          />
        </div>
      ) : null}

      {busy ? (
        <p role="status" className="mb-4 text-xs text-content-muted">
          Enviando…
        </p>
      ) : null}

      {error ? <RequestAlert message={error} /> : null}

      {receipts.isLoading ? (
        <p className="text-sm text-content-muted">Carregando comprovantes…</p>
      ) : receipts.error ? (
        <RequestAlert message={getApiError(receipts.error)} />
      ) : list.length === 0 ? (
        <p className="rounded-xl border border-surface-border bg-surface-elevated px-4 py-6 text-center text-sm text-content-muted">
          {emptyText}
        </p>
      ) : (
        <ul
          aria-label="Comprovantes anexados"
          className="divide-y divide-surface-border rounded-xl border border-surface-border"
        >
          {list.map((receipt) => (
            <li key={receipt.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-brand-500/10 text-brand-400">
                {receipt.mimeType.startsWith('image/') ? (
                  <ImageIcon className="size-4" />
                ) : (
                  <FileText className="size-4" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <a
                  href={source.fileUrl(receipt.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="block cursor-pointer truncate text-sm font-medium text-brand-300 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  {receipt.fileName}
                </a>
                <span className="mt-0.5 block text-xs text-content-faint">
                  {formatSize(receipt.sizeBytes)} · {formatDate(receipt.createdAt, timeZone)}
                  {receipt.uploadedBy
                    ? ` · ${receipt.uploadedBy.firstName} ${receipt.uploadedBy.lastName}`
                    : ''}
                  {receipt.origin === 'LINK' ? ' · de um link' : ''}
                </span>
                {/* Prometer "verificado" sem antivírus seria mentira; o operador merece saber. */}
                {receipt.scanStatus === 'NOT_SCANNED' ? (
                  <span className="mt-1 flex items-center gap-1.5 text-xs text-warning">
                    <ShieldAlert className="size-3.5" />
                    Sem varredura de antivírus neste ambiente
                  </span>
                ) : null}
              </span>
              {canManage ? (
                <IconButton
                  label="Remover"
                  icon={<Trash2 />}
                  tone="danger"
                  onClick={() => setRemoving(receipt)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-7 flex justify-end border-t border-surface-border pt-5">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Concluir
        </Button>
      </div>

      <CameraCapture
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        title="Fotografar comprovante"
        fileName={nextPhotoName(list.map((receipt) => receipt.fileName))}
        onCapture={(file) => void upload([file])}
      />

      {removing ? (
        <RemoveReceiptDialog
          receipt={removing}
          remove={source.remove}
          onClose={() => setRemoving(undefined)}
          onRemoved={async () => {
            await receipts.refetch();
            setRemoving(undefined);
          }}
        />
      ) : null}
    </Modal>
  );
}

function RemoveReceiptDialog({
  receipt,
  remove,
  onClose,
  onRemoved,
}: {
  receipt: StoredReceipt;
  remove: (id: string) => Promise<void>;
  onClose: () => void;
  onRemoved: () => Promise<void>;
}) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setError(undefined);
    setBusy(true);
    try {
      await remove(receipt.id);
      await onRemoved();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Remover comprovante"
      description="O arquivo sai junto."
    >
      <p className="text-sm leading-6 text-content-secondary">
        Remover <strong>{receipt.fileName}</strong>? O arquivo é apagado do armazenamento e não há
        como recuperá-lo por aqui.
      </p>
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={busy}
          onClick={() => void confirm()}
        >
          Remover comprovante
        </Button>
      </div>
    </Modal>
  );
}
